// core/decorators/ui.ts
export function UI(
  selectorMap: Record<string, string>,
  options?: { shadowRoot?: boolean },
) {
  const { shadowRoot = true } = options || {};

  return function <T extends HTMLElement, V extends object>(
    target: ClassAccessorDecoratorTarget<T, V>,
    context: ClassAccessorDecoratorContext<T, V>,
  ) {
    const name = String(context.name);
    // Symbole pour stocker l'objet UI une fois créé
    const uiCacheKey = Symbol(name);

    return {
      get(this: T): V {
        // 1. Si l'objet UI existe déjà, on le retourne
        if ((this as any)[uiCacheKey]) {
          return (this as any)[uiCacheKey];
        }

        const root = shadowRoot ? this.shadowRoot || this : this;

        // 2. On crée un objet vide
        const uiObject: any = {};

        // 3. On utilise un Map interne pour stocker les résultats des querySelector
        //    pour ne pas les refaire à chaque accès (Cache granulaire)
        const domCache = new Map<string, Element | null>();

        // 4. On définit dynamiquement des getters pour chaque clé
        for (const [key, selector] of Object.entries(selectorMap)) {
          Object.defineProperty(uiObject, key, {
            configurable: true,
            enumerable: true,
            get: () => {
              // A. Si on a déjà cherché cet élément précis, on le rend
              if (domCache.has(key)) {
                return domCache.get(key);
              }

              // B. Sinon, on fait le querySelector (LAZY)
              const element = root.querySelector(selector);

              // C. On le met en cache
              domCache.set(key, element);

              return element;
            },
            // Permet d'écraser manuellement si besoin : this.#_ui.icon = ...
            set: (value: Element | null) => {
              domCache.set(key, value);
            },
          });
        }

        // 5. On stocke l'objet configuré sur l'instance et on le retourne
        (this as any)[uiCacheKey] = uiObject;
        return uiObject as V;
      },
    };
  };
}
